import { useState, useMemo, useCallback, useEffect } from "react";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { Action } from "redux";
import { useDispatch, useSelector } from "react-redux";
import { useSpinner } from "../../customHooks";
import { RootState } from "../../store";
import { Status } from "../../common/constants";

import { dateRangePresets } from "./analyticsData";
import {
  DateRangePreset,
  IKpiCard,
  IOrgBreakdown,
  IFirmwareDistribution,
  IAlertSeverityData,
  IAlertSourceData,
  IUpdateStatusData,
  IIntegrationAdoption,
  IOrgDetailData,
  ITotalAlertsKpi,
  IOrgAggregates,
} from "./analytics.types";

import {
  selector as analyticsFilterSelector,
  setAnalyticsPreset,
  setAnalyticsCustomDateRange,
} from "./slices/analyticsFilterSlice";

import {
  selector as organizationsAnalyticsSelector,
  getOrganizationsAnalytics,
} from "./slices/organizationsAnalyticsSlice";

import {
  selector as usersAnalyticsSelector,
  getUsersAnalytics,
} from "./slices/usersAnalyticsSlice";

import {
  selector as deviceAnalyticsSelector,
  getDeviceAnalytics,
} from "./slices/deviceAnalyticsSlice";

import {
  selector as alertsAnalyticsSelector,
  getAlertsAnalytics,
} from "./slices/alertsAnalyticsSlice";

import {
  selector as integrationsAnalyticsSelector,
  getIntegrationsAnalytics,
} from "./slices/integrationsAnalyticsSlice";

import {
  selector as versionDistributionSelector,
  getVersionDistribution,
} from "./slices/versionDistributionSlice";
import { formatSeverityLabel, getFailedAnalyticsMessage, getSeverityColor } from "./analyticsUtils";

const useAnalytics = () => {
  const dispatch: ThunkDispatch<RootState, undefined, Action> = useDispatch();

  const { dateRange } = useSelector(
    analyticsFilterSelector
  );
  const organizationsAnalyticsState = useSelector(
    organizationsAnalyticsSelector
  );
  const usersAnalyticsState = useSelector(
    usersAnalyticsSelector
  );
  const deviceAnalyticsState = useSelector(
    deviceAnalyticsSelector
  );
  const alertsAnalyticsState = useSelector(
    alertsAnalyticsSelector
  );
  const integrationsAnalyticsState = useSelector(
    integrationsAnalyticsSelector
  );
  const versionDistributionState = useSelector(
    versionDistributionSelector
  );

  const [orgSortField, setOrgSortField] =
    useState<keyof IOrgBreakdown>("alertsSent");
  const [orgSortDirection, setOrgSortDirection] = useState<"asc" | "desc">(
    "desc",
  );
  const [expandedOrgId, setExpandedOrgId] = useState<string | null>(null);

  const allAnalyticsStatuses = [
    organizationsAnalyticsState.status,
    usersAnalyticsState.status,
    deviceAnalyticsState.status,
    alertsAnalyticsState.status,
    integrationsAnalyticsState.status,
    versionDistributionState.status,
  ];

  const overallAnalyticsStatus: string = useMemo(() => {
    if (
      allAnalyticsStatuses.some(
        (currentStatus) => currentStatus === Status.PENDING,
      )
    ) {
      return Status.PENDING;
    }

    if (
      allAnalyticsStatuses.every(
        (currentStatus) => currentStatus === Status.IDLE,
      )
    ) {
      return Status.IDLE;
    }

    return Status.SUCCESS;
  }, [allAnalyticsStatuses]);

  useSpinner(overallAnalyticsStatus);

 useEffect(() => {
  if (organizationsAnalyticsState.status === Status.IDLE) {
    dispatch(getOrganizationsAnalytics());
  }

  if (integrationsAnalyticsState.status === Status.IDLE) {
    dispatch(getIntegrationsAnalytics());
  }

  if (versionDistributionState.status === Status.IDLE) {
    dispatch(getVersionDistribution());
  }

  if (usersAnalyticsState.status === Status.IDLE) {
    dispatch(getUsersAnalytics());
  }
}, [
  dispatch,
  organizationsAnalyticsState.status,
  integrationsAnalyticsState.status,
  versionDistributionState.status,
  usersAnalyticsState.status,
]);

  useEffect(() => {
  if (dateRange.preset === "custom") {
    const hasValidStartDate = Boolean(dateRange.startDate);
    const hasValidEndDate = Boolean(dateRange.endDate);

    if (!hasValidStartDate || !hasValidEndDate) {
      return;
    }

    const normalizedStartDate = new Date(dateRange.startDate);
    const normalizedEndDate = new Date(dateRange.endDate);

    const hasInvalidDateValue =
      Number.isNaN(normalizedStartDate.getTime()) ||
      Number.isNaN(normalizedEndDate.getTime());

    if (hasInvalidDateValue || normalizedStartDate > normalizedEndDate) {
      return;
    }
  }

  dispatch(getDeviceAnalytics(dateRange));
  dispatch(getAlertsAnalytics(dateRange));
}, [
  dispatch,
  dateRange.preset,
  dateRange.startDate,
  dateRange.endDate,
]);

  const handlePresetChange = useCallback(
    (preset: DateRangePreset) => {
      dispatch(setAnalyticsPreset(preset));
    },
    [dispatch],
  );

  const handleApplyCustomDateRange = useCallback(
  (startDate: string, endDate: string) => {
    dispatch(setAnalyticsCustomDateRange({ startDate, endDate }));
  },
  [dispatch],
);

  const kpis: IKpiCard[] = useMemo(() => {
    const totalDevicesCount = Number(deviceAnalyticsState.totalDevices || 0);
    const onlineDevicesCount = Number(deviceAnalyticsState.onlineDevices || 0);
    const onlineDevicesPercentage =
      totalDevicesCount > 0
        ? Math.round((onlineDevicesCount / totalDevicesCount) * 1000) / 10
        : 0;

    return [
      {
        title: "Total Organizations",
        value: Number(organizationsAnalyticsState.totalOrgCount || 0),
        subtitle: `${Number(organizationsAnalyticsState.activeOrgCount || 0)} Active, ${Number(
          organizationsAnalyticsState.disabledOrgCount || 0,
        )} Disabled`,
        icon: "organizations",
      },
      {
        title: "Total Devices",
        value: totalDevicesCount,
        subtitle: "Across all organizations",
        icon: "devices",
      },
      {
        title: "Online Devices",
        value: onlineDevicesCount,
        subtitle: `${onlineDevicesPercentage}% online rate`,
        icon: "online",
      },
      {
        title: "Total Users",
        value: Number(usersAnalyticsState.totalUsersCount || 0),
        subtitle: "Across all organizations",
        icon: "users",
      },
    ];
  }, [
    organizationsAnalyticsState.totalOrgCount,
    organizationsAnalyticsState.activeOrgCount,
    organizationsAnalyticsState.disabledOrgCount,
    deviceAnalyticsState.totalDevices,
    deviceAnalyticsState.onlineDevices,
    usersAnalyticsState.totalUsersCount,
  ]);

  const totalAlertsKpi: ITotalAlertsKpi = useMemo(() => {
    return {
      total: Number(alertsAnalyticsState.totalAlertsCount || 0),
      trendDirection: "neutral",
    };
  }, [alertsAnalyticsState.totalAlertsCount]);

  const organizationWiseUsersMap = useMemo<Map<string, number>>(() => {
    return new Map<string, number>(
      (usersAnalyticsState.organizationWiseUsers || []).map(
        (organizationWiseUser) => [
          String(organizationWiseUser.orgId),
          Number(organizationWiseUser.users || 0),
        ],
      ),
    );
  }, [usersAnalyticsState.organizationWiseUsers]);

  const organizationWiseDevicesMap = useMemo<
    Map<
      string,
      {
        totalDevices: number;
        onlineDevices: number;
        devicesAdded: number;
        deviceGrowth: { label: string; count: number }[];
        deviceGrowthDateGroupType: string;
      }
    >
  >(() => {
    return new Map(
      (deviceAnalyticsState.organizationWiseDevices || []).map(
        (organizationWiseDevice) => [
          String(organizationWiseDevice.orgId),
          {
            totalDevices: Number(organizationWiseDevice.totalDevices || 0),
            onlineDevices: Number(organizationWiseDevice.onlineDevices || 0),
            devicesAdded: Number(organizationWiseDevice.devicesAdded || 0),
            deviceGrowth: organizationWiseDevice.deviceGrowth || [],
            deviceGrowthDateGroupType: String(
              organizationWiseDevice.deviceGrowthDateGroupType || "",
            ),
          },
        ],
      ),
    );
  }, [deviceAnalyticsState.organizationWiseDevices]);

  const organizationAlertStatisticsMap = useMemo<
    Map<
      string,
      {
        orgName: string;
        totalAlerts: number;
        successCount: number;
        failedCount: number;
        topSource: string;
        successRate: number;
        sourceStats: { source: string; count: number }[];
        severityStats: { severity: string; count: number }[];
      }
    >
  >(() => {
    return new Map(
      (alertsAnalyticsState.orgStatistics || []).map(
        (organizationAlertStat) => [
          String(organizationAlertStat.orgId),
          {
            orgName: String(organizationAlertStat.orgName || ""),
            totalAlerts: Number(organizationAlertStat.totalAlerts || 0),
            successCount: Number(organizationAlertStat.successCount || 0),
            failedCount: Number(organizationAlertStat.failedCount || 0),
            topSource: String(organizationAlertStat.topSource || ""),
            successRate: Number(organizationAlertStat.successRate || 0),
            sourceStats: organizationAlertStat.sourceStats || [],
            severityStats: organizationAlertStat.severityStats || [],
          },
        ],
      ),
    );
  }, [alertsAnalyticsState.orgStatistics]);

  const organizationRows: IOrgBreakdown[] = useMemo(() => {
    return (organizationsAnalyticsState.organizations || [])
      .filter((organizationRecord) =>
        String(organizationRecord.orgName || "").trim(),
      )
      .map((organizationRecord) => {
        const organizationId = String(
          organizationRecord.orgId || organizationRecord.orgID || "",
        );
        const deviceMetrics = organizationWiseDevicesMap.get(organizationId);
        const alertMetrics = organizationAlertStatisticsMap.get(organizationId);

        const totalDevicesCount = Number(deviceMetrics?.totalDevices || 0);
        const onlineDevicesCount = Number(deviceMetrics?.onlineDevices || 0);

        return {
          orgId: organizationId,
          orgName: String(organizationRecord.orgName || "").trim(),
          totalDevices: totalDevicesCount,
          onlineDevices: onlineDevicesCount,
          offlineDevices: Math.max(0, totalDevicesCount - onlineDevicesCount),
          totalUsers: Number(organizationWiseUsersMap.get(organizationId) || 0),
          activeAlerts: 0,
          status: "Active",
          alertsSent: Number(alertMetrics?.totalAlerts || 0),
          devicesAdded: Number(deviceMetrics?.devicesAdded || 0),
          alertSuccessRate: Number(alertMetrics?.successRate || 0),
          topAlertSource: String(alertMetrics?.topSource || "N/A"),
          avgAlertsPerDay: 0,
        };
      });
  }, [
    organizationsAnalyticsState.organizations,
    organizationWiseDevicesMap,
    organizationWiseUsersMap,
    organizationAlertStatisticsMap,
  ]);

  const sortedOrgs: IOrgBreakdown[] = useMemo(() => {
    return [...organizationRows].sort(
      (firstOrganization, secondOrganization) => {
        const firstValue = firstOrganization[orgSortField];
        const secondValue = secondOrganization[orgSortField];

        if (typeof firstValue === "number" && typeof secondValue === "number") {
          return orgSortDirection === "desc"
            ? secondValue - firstValue
            : firstValue - secondValue;
        }

        return orgSortDirection === "desc"
          ? String(secondValue).localeCompare(String(firstValue))
          : String(firstValue).localeCompare(String(secondValue));
      },
    );
  }, [organizationRows, orgSortField, orgSortDirection]);

  const handleOrgSort = useCallback((field: keyof IOrgBreakdown) => {
    setOrgSortField((previousSortField) => {
      if (previousSortField === field) {
        setOrgSortDirection((previousDirection) =>
          previousDirection === "desc" ? "asc" : "desc",
        );
        return previousSortField;
      }

      setOrgSortDirection("desc");
      return field;
    });
  }, []);

  const handleOrgExpand = useCallback((orgId: string) => {
    setExpandedOrgId((previousExpandedOrgId) =>
      previousExpandedOrgId === orgId ? null : orgId,
    );
  }, []);

  const expandedOrgDetail: IOrgDetailData | null = useMemo(() => {
    if (!expandedOrgId) {
      return null;
    }

    const alertMetrics = organizationAlertStatisticsMap.get(expandedOrgId);
    const deviceMetrics = organizationWiseDevicesMap.get(expandedOrgId);

    return {
      orgId: expandedOrgId,
      alertsBySeverity: (alertMetrics?.severityStats || []).map(
        (severityStat) => ({
          severity: formatSeverityLabel(String(severityStat.severity || "")),
          count: Number(severityStat.count || 0),
          color: getSeverityColor(String(severityStat.severity || "")),
        }),
      ),
      alertsBySource: (alertMetrics?.sourceStats || []).map((sourceStat) => ({
        source: String(sourceStat.source || ""),
        count: Number(sourceStat.count || 0),
      })),
      deviceGrowth: (deviceMetrics?.deviceGrowth || []).map((growthPoint) => ({
      label: String(growthPoint.label || ""),
      count: Number(growthPoint.count || 0),
    })),
      alertSuccessFailure: {
        success: Number(alertMetrics?.successCount || 0),
        failed: Number(alertMetrics?.failedCount || 0),
        total: Number(alertMetrics?.totalAlerts || 0),
      },
    };
  }, [
    expandedOrgId,
    organizationAlertStatisticsMap,
    organizationWiseDevicesMap,
  ]);

  const totalDevices: number = Number(deviceAnalyticsState.totalDevices || 0);
  const onlineDevices: number = Number(deviceAnalyticsState.onlineDevices || 0);
  const offlineDevices: number = Number(
    deviceAnalyticsState.offlineDevices || 0,
  );
  const onlinePercentage: number =
    totalDevices > 0
      ? Math.round((onlineDevices / totalDevices) * 1000) / 10
      : 0;

  const firmwareVersions: IFirmwareDistribution[] =
    versionDistributionState.firmwareVersions || [];
  const iotAgentVersions: IFirmwareDistribution[] =
    versionDistributionState.iotAgentVersions || [];

  const alertsBySeverity: IAlertSeverityData[] =
    alertsAnalyticsState.alertsBySeverity || [];
  const alertsBySource: IAlertSourceData[] =
    alertsAnalyticsState.alertsBySource || [];
  const totalActiveAlerts: number = Number(
    alertsAnalyticsState.totalActiveAlerts || 0,
  );

  const fwUpdateStatus: IUpdateStatusData[] =
    versionDistributionState.firmwareUpdateStatus || [];
  const iotUpdateStatus: IUpdateStatusData[] =
    versionDistributionState.iotAgentUpdateStatus || [];

  const integrations: IIntegrationAdoption[] =
    integrationsAnalyticsState.integrations || [];

  const orgAggregates: IOrgAggregates = useMemo(() => {
    const totalDevicesAdded = organizationRows.reduce(
      (totalDevicesAddedCount, currentOrganization) =>
        totalDevicesAddedCount + Number(currentOrganization.devicesAdded || 0),
      0,
    );

    return {
      totalAlertsSent: Number(
        alertsAnalyticsState.globalAlertStats?.totalAlerts || 0,
      ),
      totalDevicesAdded,
      avgSuccessRate: Number(
        alertsAnalyticsState.globalAlertStats?.avgSuccessRate || 0,
      ),
    };
  }, [organizationRows, alertsAnalyticsState.globalAlertStats]);

  const error =
    organizationsAnalyticsState.error ||
    usersAnalyticsState.error ||
    deviceAnalyticsState.error ||
    alertsAnalyticsState.error ||
    integrationsAnalyticsState.error ||
    versionDistributionState.error ||
    null;

 const failedAnalyticsMessage = getFailedAnalyticsMessage({
  organizationsError: organizationsAnalyticsState.error,
  usersError: usersAnalyticsState.error,
  deviceError: deviceAnalyticsState.error,
  alertsError: alertsAnalyticsState.error,
  integrationsError: integrationsAnalyticsState.error,
  versionDistributionError: versionDistributionState.error,
});

  const isLoading = overallAnalyticsStatus === Status.PENDING;

  return {
    isLoading,
    error,
    dateRange,
    dateRangePresets,
    handlePresetChange,
    handleApplyCustomDateRange,
    kpis,
    totalAlertsKpi,
    sortedOrgs,
    orgSortField,
    orgSortDirection,
    handleOrgSort,
    expandedOrgId,
    handleOrgExpand,
    expandedOrgDetail,
    orgAggregates,
    totalDevices,
    onlineDevices,
    offlineDevices,
    onlinePercentage,
    firmwareVersions,
    iotAgentVersions,
    alertsBySeverity,
    alertsBySource,
    totalActiveAlerts,
    fwUpdateStatus,
    iotUpdateStatus,
    integrations,
    failedAnalyticsMessage,
  };
};

export default useAnalytics;
